import { useState, useEffect, useCallback, useRef } from 'react';
import {
  checkThomas, stageUpload, fileTriageItem,
  skipTriageItem, getPendingTriageItems,
} from '../api/triageApi';
import { getItems, createItem, updateItem, ensureFIGVault, FIG_LISTS } from '../api/figApi';
import { callJarvis } from './jarvisApi';
import { isCurrentPolicy, currentFirst } from '../utils/policySelect';

// ============================================================
// TAXONOMY DATA — from DefineHoly_Taxonomy_v1 (locked)
// ============================================================

const FIG_STATIC_ENTITIES = [
  { name: 'FIG LLC', type: 'Agency' },
  { name: 'CRC Group', type: 'Vendor' },
  { name: 'Colony Insurance Company', type: 'Carrier' },
  { name: 'Starr Surplus Lines', type: 'Carrier' },
  { name: 'AIG Specialty Insurance Company', type: 'Carrier' },
  { name: 'Texas Mutual Insurance Company', type: 'Carrier' },
  { name: 'Allstate', type: 'Carrier' },
  { name: 'Everlake Assurance', type: 'Carrier' },
];

const FAMILY_ENTITIES = [
  { name: 'Marc', type: 'Family Member' },
  { name: 'Angela', type: 'Family Member' },
  { name: 'Taylor', type: 'Family Member' },
  { name: 'Levi', type: 'Family Member' },
  { name: 'John', type: 'Family Member' },
  { name: 'Household', type: 'Household' },
];

const FIG_DOC_TYPES = [
  'Policies',
  'Underwriting/Claims',
  'Billing',
  'Certificates',
  'Correspondence',
  'Professional',
  'Contracts',
  'Identity',
  'General',
];

const FAMILY_DOC_TYPES = [
  'Policies',
  'Claims',
  'Financial',
  'Correspondence',
  'Professional Dev',
  'Contracts',
  'Medical',
  'Identity',
  'General',
];

const DOC_TYPE_HINTS = {
  'Policies': 'Is this about coverage? Dec pages, binders, endorsements, quotes.',
  'Underwriting/Claims': 'Getting/keeping coverage, or a loss? Applications, loss runs, claim letters.',
  'Billing': 'Is money moving? Invoices, payments, premium finance.',
  'Certificates': 'Auto-populated COIs. Rarely filed manually.',
  'Correspondence': 'Just communication? General emails, letters, notes.',
  'Professional': 'Proves someone is qualified? Licenses, certs, permits.',
  'Contracts': 'Did someone agree to terms? Appointments, agreements.',
  'Identity': 'What makes this entity legally real? Articles of org, EIN.',
  'General': 'None of the above fit. Catch-all.',
  'Claims': 'Did a loss happen? Claim docs, adjuster reports.',
  'Financial': 'Money moving in personal life? Tax returns, bank statements.',
  'Professional Dev': 'Proves someone is qualified? Degrees, transcripts, CE.',
  'Medical': 'About health? Records, vaccinations, prescriptions.',
};

// ============================================================
// TRIAGE QUEUE COMPONENT
// ============================================================

export default function TriageQueue({ getToken, userEmail, onBack, onAddClient }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Filing state
  const [filingStep, setFilingStep] = useState(0);
  const [selectedSide, setSelectedSide] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [selectedPolicyDocType, setSelectedPolicyDocType] = useState('');
  const [selectedLOB, setSelectedLOB] = useState('');
  const [selectedEffDate, setSelectedEffDate] = useState('');
  const [selectedExpDate, setSelectedExpDate] = useState('');
  const [selectedLayer, setSelectedLayer] = useState('');
  const [filing, setFiling] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [existingFilesLoading, setExistingFilesLoading] = useState(false);
  const [emailExpanded, setEmailExpanded] = useState(false);
  const [jbbAnalysis, setJbbAnalysis] = useState(null);
  const [jbbLoading, setJbbLoading] = useState(false);
  const [policyMatch, setPolicyMatch] = useState(null); // { policies, selected, comparison, checkedFields }
  const [policyMatchLoading, setPolicyMatchLoading] = useState(false);
  const [filedItemId, setFiledItemId] = useState(null); // TriageQueue item id after filing
  const [linkPolicies, setLinkPolicies] = useState([]); // existing policies for linking
  const [linkLoading, setLinkLoading] = useState(false);
  const [entitySearch, setEntitySearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({ Client: false, Agency: true, Vendor: true, Carrier: true, 'Family Member': false, Household: true });
  const [lobOptions, setLobOptions] = useState([]); // [{ name, abbr }]
  const [figClients, setFigClients] = useState([]);
  const [showLOBHelp, setShowLOBHelp] = useState(false);

  // Default LOBs with abbreviations to seed if list is empty
  const DEFAULT_LOBS = [
    { name: 'General Liability', abbr: 'GL' },
    { name: 'Excess Liability', abbr: 'XS' },
    { name: 'Workers Compensation', abbr: 'WC' },
    { name: 'Commercial Auto', abbr: 'CA' },
    { name: 'Commercial Property', abbr: 'CP' },
    { name: 'Inland Marine', abbr: 'IM' },
    { name: 'Professional Liability', abbr: 'PL' },
    { name: 'Umbrella', abbr: 'UMB' },
    { name: 'Business Owners Policy', abbr: 'BOP' },
    { name: 'Crime/Fidelity', abbr: 'CF' },
    { name: 'Cyber Liability', abbr: 'CY' },
    { name: 'Directors & Officers', abbr: 'DO' },
    { name: 'Employment Practices Liability', abbr: 'EPL' },
    { name: 'Travel', abbr: 'TRV' },
  ];

  // Load LOB options from SharePoint
  const loadLOBs = useCallback(async () => {
    try {
      const token = await getToken();
      let items = await getItems(token, FIG_LISTS.LINES_OF_BUSINESS);
      if (items.length === 0) {
        // Seed defaults
        for (const { name, abbr } of DEFAULT_LOBS) {
          await createItem(token, FIG_LISTS.LINES_OF_BUSINESS, { Title: name, Abbreviation: abbr });
        }
        items = await getItems(token, FIG_LISTS.LINES_OF_BUSINESS);
      }
      setLobOptions(
        items.filter(i => i.Title).map(i => ({ name: i.Title, abbr: i.Abbreviation || '' })).sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (err) {
      console.error('[Triage] Failed to load LOBs:', err.message);
    }
  }, [getToken]);

  useEffect(() => { loadLOBs(); }, [loadLOBs]);

  // Load FIG clients from SharePoint
  const loadFigClients = useCallback(async () => {
    try {
      const token = await getToken();
      const items = await getItems(token, FIG_LISTS.INSUREDS);
      setFigClients(
        items.filter(i => i.CompanyName).map(i => ({ name: i.CompanyName, type: 'Client', id: i.id }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (err) {
      console.error('[Triage] Failed to load FIG clients:', err.message);
    }
  }, [getToken]);

  useEffect(() => { loadFigClients(); }, [loadFigClients]);

  function handleAddClient() {
    if (onAddClient) onAddClient();
  }

  function getLOBAbbr(lobName) {
    const match = lobOptions.find(l => l.name === lobName);
    return match?.abbr || '';
  }

  const GRAPH = 'https://graph.microsoft.com/v1.0';

  async function loadExistingFiles() {
    setExistingFiles([]);
    setExistingFilesLoading(true);
    try {
      const token = await getToken();
      const driveId = await ensureFIGVault(token);
      // Build dest folder path (same logic as triageApi)
      let destFolder;
      if (selectedSide === 'FIG') {
        const ef = `Clients/${selectedEntity?.name}`;
        destFolder = selectedDocType === 'Policies' && selectedLOB
          ? `${ef}/Policies/${selectedLOB}` : `${ef}/${selectedDocType}`;
      } else {
        destFolder = `Family/${selectedEntity?.name}/${selectedDocType}`;
      }
      const res = await fetch(
        `${GRAPH}/drives/${driveId}/root:/${destFolder}:/children?$select=name,lastModifiedDateTime,size&$top=20&$orderby=lastModifiedDateTime desc`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setExistingFiles((data.value || []).filter(f => !f.folder));
      }
    } catch {
      // Folder may not exist yet — that's fine
    } finally {
      setExistingFilesLoading(false);
    }
  }

  function goToConfirm() {
    setFilingStep(4);
    loadExistingFiles();
  }

  async function handleAddLOB() {
    const name = window.prompt('New Line of Business name:');
    if (!name || !name.trim()) return;
    const abbr = window.prompt('Abbreviation (e.g. GL, WC, CP):') || '';
    try {
      const token = await getToken();
      await createItem(token, FIG_LISTS.LINES_OF_BUSINESS, { Title: name.trim(), Abbreviation: abbr.trim() });
      setLobOptions(prev => [...prev, { name: name.trim(), abbr: abbr.trim() }].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedLOB(name.trim());
    } catch (err) {
      console.error('[Triage] Failed to add LOB:', err.message);
    }
  }

  // Load pending items
  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const pending = await getPendingTriageItems(token);
      setItems(pending);
      setCurrentIndex(0);
      resetFiling();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { loadItems(); }, [loadItems]);

  function resetFiling() {
    setFilingStep(0);
    setSelectedSide(null);
    setSelectedEntity(null);
    setSelectedDocType(null);
    setSelectedPolicyDocType('');
    setSelectedLOB('');
    setSelectedEffDate('');
    setSelectedExpDate('');
    setSelectedLayer('');
    setEntitySearch('');
    setEmailExpanded(false);
    setFiledItemId(null);
    setLinkPolicies([]);
    setJbbAnalysis(null);
    setPolicyMatch(null);
  }

  async function handleReadWithJBB() {
    const item = items[currentIndex];
    if (!item?.StagingPath) return;
    setJbbLoading(true);
    setJbbAnalysis(null);
    try {
      const token = await getToken();
      const driveId = await ensureFIGVault(token);

      // Fetch PDF as blob from SharePoint
      // Use FileUrl if available (works for both staged and filed items), fall back to StagingPath
      let fileUrl;
      if (item.FileUrl) {
        // FileUrl is a SharePoint webUrl — use the item ID approach instead
        // First resolve the item by path
        const encodedPath = item.StagingPath.split('/').map(encodeURIComponent).join('/');
        fileUrl = `${GRAPH}/drives/${driveId}/root:/${encodedPath}:/content`;
      } else {
        const encodedPath = item.StagingPath.split('/').map(encodeURIComponent).join('/');
        fileUrl = `${GRAPH}/drives/${driveId}/root:/${encodedPath}:/content`;
      }
      console.log('[JBB] Fetching file:', fileUrl, 'StagingPath:', item.StagingPath);
      const fileRes = await fetch(fileUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!fileRes.ok) {
        const errText = await fileRes.text().catch(() => '');
        throw new Error(`Failed to fetch file (${fileRes.status}): ${errText.substring(0, 200)}`);
      }
      const blob = await fileRes.blob();

      // Convert to base64
      const arrayBuf = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuf);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const base64 = btoa(binary);

      // Send to JBB via callJarvis
      const response = await callJarvis({
        system: 'You are a commercial insurance document analyst. Read the attached document and respond with ONLY valid JSON, no markdown, no preamble. Extract: {carrierName, policyNumber, effectiveDate, expirationDate, lob, documentType, namedInsured, limits, summary} where lob is one of: General Liability, Commercial Property, Workers Compensation, Commercial Auto, Excess Liability, Umbrella, Other. documentType is one of: Quote, Binder, Policy, Endorsement, Certificate, Invoice, Notice, Other. limits is an object of limit name/value pairs. summary is a 1-2 sentence plain English description of what this document is.',
        messages: [{
          role: 'user',
          content: [
            {
              type: 'document',
              source: { type: 'base64', media_type: 'application/pdf', data: base64 },
            },
            {
              type: 'text',
              text: 'Read this insurance document and extract the requested fields as JSON.',
            },
          ],
        }],
        max_tokens: 1500,
      });

      const text = response.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';
      const jsonStr = text.replace(/```json?\n?/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(jsonStr);
      console.log('[JBB] Analysis result:', parsed);
      setJbbAnalysis(parsed);
    } catch (err) {
      console.error('[JBB] Read failed:', err);
      setJbbAnalysis({ error: err.message });
    } finally {
      setJbbLoading(false);
    }
  }

  function applyJbbAnalysis() {
    if (!jbbAnalysis || jbbAnalysis.error) return;
    const a = jbbAnalysis;

    // Set side to FIG (insurance docs are always FIG)
    setSelectedSide('FIG');
    setFilingStep(1);

    // Match namedInsured to figClients
    if (a.namedInsured) {
      const query = a.namedInsured.toLowerCase();
      const match = figClients.find(c => c.name.toLowerCase().includes(query) || query.includes(c.name.toLowerCase()));
      if (match) {
        setSelectedEntity(match);
        setFilingStep(2);

        // Set docType
        const docTypeMap = { 'Quote': 'Policies', 'Binder': 'Policies', 'Policy': 'Policies',
          'Endorsement': 'Policies', 'Certificate': 'Certificates', 'Invoice': 'Billing', 'Notice': 'Correspondence' };
        const mappedDocType = docTypeMap[a.documentType] || 'Policies';
        setSelectedDocType(mappedDocType);
        setFilingStep(3);

        // Set policy details
        if (a.documentType) setSelectedPolicyDocType(a.documentType);
        if (a.lob) setSelectedLOB(a.lob);
        // Convert dates to YYYY-MM-DD for HTML date inputs
        const toISODate = (str) => {
          if (!str) return '';
          const d = new Date(str);
          if (isNaN(d)) return '';
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        if (a.effectiveDate) setSelectedEffDate(toISODate(a.effectiveDate));
        if (a.expirationDate) setSelectedExpDate(toISODate(a.expirationDate));
      }
    }
  }

  // JBB → Policy field mapping
  const JBB_POLICY_FIELDS = [
    { jbb: 'carrierName', policy: 'Carrier', label: 'Carrier' },
    { jbb: 'policyNumber', policy: 'PolicyNumber', label: 'Policy #' },
    { jbb: 'effectiveDate', policy: 'EffectiveDate', label: 'Effective Date' },
    { jbb: 'expirationDate', policy: 'ExpirationDate', label: 'Expiration Date' },
    { jbb: 'lob', policy: 'LOB', label: 'Line of Business' },
    { jbb: 'documentType', policy: 'Status', label: 'Status' },
  ];
  const JBB_LIMIT_FIELDS = [
    { jbb: 'Each Occurrence', policy: 'EachOccurrenceLimit', label: 'Each Occurrence' },
    { jbb: 'General Aggregate', policy: 'GeneralAggregateLimit', label: 'General Aggregate' },
    { jbb: 'Products/Completed Ops', policy: 'ProductsCompOpsLimit', label: 'Products/Comp Ops' },
    { jbb: 'Personal & Advertising Injury', policy: 'PersonalAdvInjuryLimit', label: 'Personal & Adv Injury' },
    { jbb: 'Fire Damage', policy: 'FireDamageLimit', label: 'Fire Damage' },
    { jbb: 'Medical Expense', policy: 'MedExpenseLimit', label: 'Medical Expense' },
    { jbb: 'Employee Benefits', policy: 'EmployeeBenefitsLimit', label: 'Employee Benefits' },
  ];

  async function handleMatchToPolicy() {
    if (!jbbAnalysis) return;
    setPolicyMatchLoading(true);
    try {
      const token = await getToken();
      const allPolicies = await getItems(token, FIG_LISTS.POLICIES);

      // Find all policies for this insured
      const insuredName = (jbbAnalysis.namedInsured || '').toLowerCase();
      const insuredPolicies = allPolicies.filter(p => {
        const entityMatch = figClients.find(c => c.id && String(c.id) === String(p.InsuredId));
        if (!entityMatch) return false;
        return entityMatch.name.toLowerCase().includes(insuredName) ||
          insuredName.includes(entityMatch.name.toLowerCase());
      });

      if (insuredPolicies.length === 0) {
        setPolicyMatch({ policies: [], selected: null, comparison: null, checkedFields: {} });
      } else {
        // Smart auto-select: policy number match > LOB match > show picker.
        // Current (Active/Bound) rows are tried first; all rows (including
        // prior-term Expired history) only when there is no current match.
        let selected = null;
        const currentPolicies = insuredPolicies.filter(isCurrentPolicy);

        // 1. Try exact policy number match
        if (jbbAnalysis.policyNumber) {
          selected = currentPolicies.find(p => p.PolicyNumber === jbbAnalysis.policyNumber)
            || insuredPolicies.find(p => p.PolicyNumber === jbbAnalysis.policyNumber);
        }

        // 2. Try LOB match
        if (!selected && jbbAnalysis.lob) {
          const jbbLob = jbbAnalysis.lob.toLowerCase();
          const lobMatch = p => (p.LOB || '').toLowerCase().includes(jbbLob) || jbbLob.includes((p.LOB || '').toLowerCase());
          const currentLobMatches = currentPolicies.filter(lobMatch);
          if (currentLobMatches.length === 1) selected = currentLobMatches[0];
          else if (currentLobMatches.length === 0) {
            const lobMatches = insuredPolicies.filter(lobMatch);
            if (lobMatches.length === 1) selected = lobMatches[0];
          }
        }

        // 3. If still no single match, default to the newest current row but show picker
        if (!selected) selected = currentFirst(insuredPolicies)[0];

        const { comparison, checkedFields } = buildComparison(selected);
        setPolicyMatch({ policies: insuredPolicies, selected, comparison, checkedFields });
      }
    } catch (err) {
      console.error('[PolicyMatch] Failed:', err);
      setPolicyMatch({ policies: [], selected: null, comparison: null, checkedFields: {}, error: err.message });
    } finally {
      setPolicyMatchLoading(false);
    }
  }

  function buildComparison(policy) {
    const a = jbbAnalysis;
    const comparison = [];
    const checkedFields = {};

    // Core fields
    for (const { jbb, policy: pKey, label } of JBB_POLICY_FIELDS) {
      const jbbVal = String(a[jbb] || '').trim();
      const polVal = String(policy[pKey] || '').trim();
      if (!jbbVal) continue;
      if (!polVal) {
        comparison.push({ key: pKey, label, jbbVal, polVal: '', status: 'new' });
        checkedFields[pKey] = true;
      } else if (polVal.toLowerCase() === jbbVal.toLowerCase()) {
        comparison.push({ key: pKey, label, jbbVal, polVal, status: 'match' });
      } else {
        comparison.push({ key: pKey, label, jbbVal, polVal, status: 'conflict' });
        checkedFields[pKey] = false;
      }
    }

    // Limits from JBB
    if (a.limits) {
      for (const { jbb, policy: pKey, label } of JBB_LIMIT_FIELDS) {
        // Find matching limit — try exact match then partial
        const limitVal = a.limits[jbb] || Object.entries(a.limits).find(
          ([k]) => k.toLowerCase().includes(jbb.toLowerCase().split('/')[0])
        )?.[1];
        if (limitVal == null) continue;
        const jbbStr = String(limitVal);
        const polVal = String(policy[pKey] || '').trim();
        if (!polVal) {
          comparison.push({ key: pKey, label, jbbVal: jbbStr, polVal: '', status: 'new' });
          checkedFields[pKey] = true;
        } else if (polVal === jbbStr || Number(polVal) === Number(jbbStr)) {
          comparison.push({ key: pKey, label, jbbVal: jbbStr, polVal, status: 'match' });
        } else {
          comparison.push({ key: pKey, label, jbbVal: jbbStr, polVal, status: 'conflict' });
          checkedFields[pKey] = false;
        }
      }
    }

    return { comparison, checkedFields };
  }

  function selectMatchPolicy(policy) {
    const { comparison, checkedFields } = buildComparison(policy);
    setPolicyMatch(prev => ({ ...prev, selected: policy, comparison, checkedFields }));
  }

  function toggleMatchField(key) {
    setPolicyMatch(prev => ({
      ...prev,
      checkedFields: { ...prev.checkedFields, [key]: !prev.checkedFields[key] },
    }));
  }

  async function applySelectedFields() {
    if (!policyMatch?.selected?.id) return;
    const fields = {};
    for (const row of policyMatch.comparison) {
      if (policyMatch.checkedFields[row.key]) {
        fields[row.key] = row.jbbVal;
      }
    }
    if (Object.keys(fields).length === 0) return;
    try {
      const token = await getToken();
      await updateItem(token, FIG_LISTS.POLICIES, policyMatch.selected.id, fields);
      setSuccessMsg(`Updated ${Object.keys(fields).length} field(s) on policy`);
      setTimeout(() => setSuccessMsg(null), 3000);
      setPolicyMatch(null);
    } catch (err) {
      setError('Failed to update policy: ' + err.message);
    }
  }

  async function createPolicyFromJBB() {
    if (!jbbAnalysis) return;
    const a = jbbAnalysis;
    // Find insured ID
    const insuredName = (a.namedInsured || '').toLowerCase();
    const match = figClients.find(c => c.name.toLowerCase().includes(insuredName) || insuredName.includes(c.name.toLowerCase()));
    if (!match?.id) { setError('Cannot find insured to create policy'); return; }
    try {
      const token = await getToken();
      const fields = {
        Title: `${a.lob || 'Policy'} - ${a.carrierName || ''}`,
        InsuredId: String(match.id),
        Carrier: a.carrierName || '',
        PolicyNumber: a.policyNumber || '',
        LOB: a.lob || '',
        EffectiveDate: a.effectiveDate || '',
        ExpirationDate: a.expirationDate || '',
        Status: a.documentType || 'Quote',
      };
      // Add limits
      if (a.limits) {
        for (const { jbb, policy: pKey } of JBB_LIMIT_FIELDS) {
          const v = a.limits[jbb] || Object.entries(a.limits).find(
            ([k]) => k.toLowerCase().includes(jbb.toLowerCase().split('/')[0])
          )?.[1];
          if (v != null) fields[pKey] = String(v);
        }
      }
      await createItem(token, FIG_LISTS.POLICIES, fields);
      setSuccessMsg('Policy created from JBB analysis');
      setTimeout(() => setSuccessMsg(null), 3000);
      setPolicyMatch(null);
    } catch (err) {
      setError('Failed to create policy: ' + err.message);
    }
  }

  async function handleCheckThomas() {
    try {
      setChecking(true);
      setError(null);
      const token = await getToken();
      const result = await checkThomas(token);
      if (result.newItems > 0) {
        setSuccessMsg(`${result.newItems} new item${result.newItems > 1 ? 's' : ''} from Thomas`);
        setTimeout(() => setSuccessMsg(null), 3000);
        await loadItems();
      } else {
        setSuccessMsg('Thomas is empty — no new items');
        setTimeout(() => setSuccessMsg(null), 3000);
      }
    } catch (err) {
      setError(`Thomas check failed: ${err.message}`);
    } finally {
      setChecking(false);
    }
  }

  async function handleUpload(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      setUploading(true);
      setError(null);
      const token = await getToken();
      let count = 0;
      for (const file of files) {
        await stageUpload(token, file, userEmail);
        count++;
      }
      setSuccessMsg(`${count} file${count > 1 ? 's' : ''} staged`);
      setTimeout(() => setSuccessMsg(null), 3000);
      await loadItems();
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleFile() {
    const item = items[currentIndex];
    if (!item || !selectedSide || !selectedEntity || !selectedDocType) return;
    try {
      setFiling(true);
      setError(null);
      const token = await getToken();
      const result = await fileTriageItem(token, item, {
        side: selectedSide,
        entityName: selectedEntity.name,
        entityType: selectedEntity.type,
        docType: selectedDocType,
        policyDocType: selectedPolicyDocType,
        lob: selectedLOB,
        lobAbbr: getLOBAbbr(selectedLOB),
        effectiveDate: selectedEffDate,
        expirationDate: selectedExpDate,
        layer: selectedLayer,
      }, userEmail);
      setSuccessMsg(`Filed → ${result.filedTo}`);
      setTimeout(() => setSuccessMsg(null), 4000);

      // Check if we should offer policy linking
      const shouldLink = selectedDocType === 'Policies' &&
        ['Quote', 'Binder', 'Policy'].includes(selectedPolicyDocType) &&
        selectedEntity?.id;

      if (shouldLink) {
        setFiledItemId(item.id);
        setFilingStep(5);
        // Load existing policies for this entity
        setLinkLoading(true);
        try {
          const policies = await getItems(token, FIG_LISTS.POLICIES);
          setLinkPolicies(policies.filter(p => String(p.InsuredId) === String(selectedEntity.id)));
        } catch { setLinkPolicies([]); }
        finally { setLinkLoading(false); }
        // Remove from queue visually but don't reset filing yet
        const newItems = items.filter((_, i) => i !== currentIndex);
        setItems(newItems);
        setCurrentIndex(Math.min(currentIndex, Math.max(0, newItems.length - 1)));
      } else if (jbbAnalysis && selectedDocType === 'Policies') {
        // After filing a policy doc with JBB data, offer to update policy record
        setFiledItemId(item.id);
        setFilingStep(6);
        const newItems = items.filter((_, i) => i !== currentIndex);
        setItems(newItems);
        setCurrentIndex(Math.min(currentIndex, Math.max(0, newItems.length - 1)));
      } else {
        const newItems = items.filter((_, i) => i !== currentIndex);
        setItems(newItems);
        setCurrentIndex(Math.min(currentIndex, Math.max(0, newItems.length - 1)));
        resetFiling();
      }
    } catch (err) {
      setError(`Filing failed: ${err.message}`);
    } finally {
      setFiling(false);
    }
  }

  async function handleSkip() {
    const item = items[currentIndex];
    if (!item) return;
    try {
      const token = await getToken();
      await skipTriageItem(token, item);
      const newItems = items.filter((_, i) => i !== currentIndex);
      setItems(newItems);
      setCurrentIndex(Math.min(currentIndex, Math.max(0, newItems.length - 1)));
      resetFiling();
    } catch (err) {
      setError(`Skip failed: ${err.message}`);
    }
  }

  function handleSelectSide(side) {
    setSelectedSide(side);
    setSelectedEntity(null);
    setSelectedDocType(null);
    setEntitySearch('');
    setFilingStep(1);
  }

  function handleSelectEntity(entity) {
    setSelectedEntity(entity);
    setSelectedDocType(null);
    setFilingStep(2);
  }

  function handleSelectDocType(docType) {
    setSelectedDocType(docType);
    if (selectedSide === 'FIG' && docType === 'Policies') {
      setFilingStep(3);
    } else {
      goToConfirm();
    }
  }

  function handleBackStep() {
    if (filingStep === 1) { setSelectedSide(null); setFilingStep(0); }
    else if (filingStep === 2) { setSelectedEntity(null); setFilingStep(1); }
    else if (filingStep === 3) { setSelectedDocType(null); setFilingStep(2); }
    else if (filingStep === 4) {
      if (selectedSide === 'FIG' && selectedDocType === 'Policies') { setFilingStep(3); }
      else { setSelectedDocType(null); setFilingStep(2); }
    }
  }

  const currentItem = items[currentIndex];
  const entities = selectedSide === 'FIG' ? [...figClients, ...FIG_STATIC_ENTITIES] : FAMILY_ENTITIES;
  const docTypes = selectedSide === 'FIG' ? FIG_DOC_TYPES : FAMILY_DOC_TYPES;

  const filteredEntities = entitySearch
    ? entities.filter(e => e.name.toLowerCase().includes(entitySearch.toLowerCase()))
    : entities;

  const entityGroups = {};
  filteredEntities.forEach(e => {
    if (!entityGroups[e.type]) entityGroups[e.type] = [];
    entityGroups[e.type].push(e);
  });

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  function fileIconSvg(color, label) {
    return (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 3C6 1.9 6.9 1 8 1H19L26 8V29C26 30.1 25.1 31 24 31H8C6.9 31 6 30.1 6 29V3Z" fill={color} opacity="0.15" stroke={color} strokeWidth="1.5"/>
        <path d="M19 1L26 8H21C19.9 8 19 7.1 19 6V1Z" fill={color} opacity="0.3"/>
        {label && <text x="16" y="22" textAnchor="middle" fontSize="7" fontWeight="700" fill={color} fontFamily="inherit">{label}</text>}
      </svg>
    );
  }

  function getFileIcon(mimeType, title) {
    const mime = (mimeType || '').toLowerCase();
    const name = (title || '').toLowerCase();
    if (mime.includes('pdf') || name.endsWith('.pdf')) return fileIconSvg('#c0392b', 'PDF');
    if (mime.includes('image') || name.match(/\.(jpg|jpeg|png|gif|webp)$/)) return '🖼️';
    if (mime.includes('word') || name.endsWith('.docx') || name.endsWith('.doc')) return fileIconSvg('#2b6cb0', 'DOC');
    if (mime.includes('excel') || mime.includes('spreadsheet') || name.match(/\.(xlsx|xls|csv)$/)) return fileIconSvg('#27ae60', 'XLS');
    if (mime.includes('message') || mime.includes('rfc822')) return '✉️';
    return fileIconSvg('#888', '');
  }

  function getSourceBadge(source) {
    if (source === 'email') return '✉️ Email';
    if (source === 'upload') return '📤 Upload';
    if (source === 'watched_folder') return '📂 Watched';
    return source || '';
  }

  return (
    <div className="Centcom">
      {/* Header */}
      <div className="Centcom-top-bar">
        <button className="Centcom-back-btn" onClick={onBack}>&larr;</button>
        <h1 className="Centcom-title">
          TRIAGE
          {items.length > 0 && <span className="Triage-badge">{items.length}</span>}
        </h1>
        <div className="Centcom-top-bar-btns">
          <button className="Triage-thomas-btn" onClick={handleCheckThomas} disabled={checking}>
            {checking ? 'Checking...' : 'Check Thomas'}
          </button>
          <button className="Triage-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading...' : '+ Upload'}
          </button>
          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleUpload} />
          <button className="Triage-thomas-btn" onClick={() => window.open('https://fragosinsurancegroupllc.sharepoint.com/sites/DefineHolyFamily/FIGVault/Forms/AllItems.aspx', '_blank')}>
            Browse Vault
          </button>
          {items.length > 0 && (
            <button style={{
              background: 'none', border: '1px solid #8B0000', borderRadius: '6px',
              color: '#c0392b', padding: '5px 12px', fontSize: '0.8rem', fontWeight: 600,
              cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.target.style.background = '#8B0000'; e.target.style.color = '#fff'; }}
            onMouseLeave={e => { e.target.style.background = 'none'; e.target.style.color = '#c0392b'; }}
            onClick={async () => {
              if (!window.confirm(`Clear all ${items.length} pending items?`)) return;
              try {
                const token = await getToken();
                for (const item of items) {
                  await skipTriageItem(token, item);
                }
                await loadItems();
                setSuccessMsg(`Cleared ${items.length} items`);
                setTimeout(() => setSuccessMsg(null), 3000);
              } catch (err) {
                setError('Clear failed: ' + err.message);
              }
            }}>
              Clear Queue
            </button>
          )}
        </div>
      </div>

      {/* Status messages */}
      {error && <div className="Triage-error">{error}</div>}
      {successMsg && <div className="Triage-success">{successMsg}</div>}

      {/* Loading */}
      {loading && <div className="Triage-loading">Loading triage queue...</div>}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="Triage-empty">
          <div className="Triage-empty-icon">📭</div>
          <div className="Triage-empty-text">Triage queue is clear</div>
          <div className="Triage-empty-sub">Forward documents to thomas@defineholy.com or upload directly</div>
        </div>
      )}

      {/* Current item card */}
      {!loading && currentItem && (() => {
        // Find sibling attachments from the same email
        const siblings = currentItem.EmailMessageId
          ? items.filter(i => i.EmailMessageId === currentItem.EmailMessageId && i.id !== currentItem.id)
          : [];
        const formatSize = (bytes) => {
          if (!bytes) return '';
          const kb = bytes / 1024;
          return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
        };
        const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, ' ')
          .replace(/&nbsp;/gi, ' ').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
          .replace(/&amp;/gi, '&').replace(/&quot;/gi, '"').replace(/&#39;/gi, "'")
          .replace(/\s+/g, ' ').trim();
        const bodyPlain = stripHtml(currentItem.EmailBody || '');
        const previewLines = bodyPlain.substring(0, 150);

        return (
        <div className="Triage-item-card">
          {items.length > 1 && (
            <div className="Triage-counter">{currentIndex + 1} of {items.length}</div>
          )}

          {/* Email context — sender, subject, date */}
          {currentItem.Source === 'email' && (
            <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #e8e0d0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                <div>
                  {currentItem.SenderName && currentItem.SenderName !== 'Manual Upload' && (
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>
                      {currentItem.SenderName}
                      {currentItem.SenderEmail && <span style={{ fontWeight: 400, color: '#888', fontSize: '0.78rem' }}> &lt;{currentItem.SenderEmail}&gt;</span>}
                    </div>
                  )}
                  {currentItem.EmailSubject && (
                    <div style={{ fontSize: '0.82rem', color: '#B8860B', fontWeight: 500, marginTop: '2px' }}>
                      {currentItem.EmailSubject}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: '0.72rem', color: '#999', flexShrink: 0, marginLeft: '12px' }}>
                  {formatDate(currentItem.ReceivedDate)}
                </span>
              </div>
              {bodyPlain && !emailExpanded && (
                <div style={{ fontSize: '0.78rem', color: '#777', lineHeight: 1.4, marginTop: '4px' }}>
                  {previewLines}{bodyPlain.length > 150 ? '...' : ''}
                </div>
              )}
              {currentItem.EmailBody && (
                <button onClick={() => setEmailExpanded(prev => !prev)}
                  style={{ background: 'none', border: 'none', color: '#4B0082', fontSize: '0.72rem',
                    cursor: 'pointer', padding: '3px 0', fontFamily: 'inherit', textDecoration: 'underline',
                    textUnderlineOffset: '2px', marginTop: '2px' }}>
                  {emailExpanded ? 'Hide email \u25B4' : 'Show full email \u25BE'}
                </button>
              )}
              {emailExpanded && currentItem.EmailBody && (
                <div style={{ marginTop: '8px', background: '#f9f7f2', border: '1px solid #e0d6c8',
                  borderRadius: '6px', padding: '12px', fontSize: '0.82rem', lineHeight: 1.6 }}>
                  <div style={{ borderBottom: '1px solid #e0d6c8', paddingBottom: '8px', marginBottom: '10px',
                    fontSize: '0.78rem', color: '#555' }}>
                    <div><strong>From:</strong> {currentItem.SenderName || ''} {currentItem.SenderEmail ? `<${currentItem.SenderEmail}>` : ''}</div>
                    <div><strong>To:</strong> thomas@defineholy.com</div>
                    {currentItem.EmailSubject && <div><strong>Subject:</strong> {currentItem.EmailSubject}</div>}
                    <div><strong>Date:</strong> {currentItem.ReceivedDate ? new Date(currentItem.ReceivedDate).toLocaleString() : ''}</div>
                  </div>
                  <div style={{ color: '#333', maxHeight: '400px', overflowY: 'auto',
                    border: '1px solid #e8e0d0', borderRadius: '4px', padding: '10px',
                    background: '#fff', wordBreak: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: currentItem.EmailBody }} />
                </div>
              )}
            </div>
          )}

          {/* Current attachment */}
          <div className="Triage-item-header">
            <span className="Triage-item-icon">{getFileIcon(currentItem.FileMimeType, currentItem.Title)}</span>
            <div className="Triage-item-info">
              <div className="Triage-item-title">
                {currentItem.Title}
                {currentItem.FileUrl && (
                  <button onClick={() => window.open(currentItem.FileUrl, '_blank')}
                    style={{ background: 'none', border: 'none', color: '#4B0082', fontSize: '0.75rem',
                      cursor: 'pointer', marginLeft: '8px', textDecoration: 'underline',
                      textUnderlineOffset: '2px', fontFamily: 'inherit', padding: 0 }}
                  >Preview ↗</button>
                )}
              </div>
              <div className="Triage-item-meta">
                <span className="Triage-item-source">{getSourceBadge(currentItem.Source)}</span>
                {currentItem.FileSize > 0 && <span style={{ color: '#888' }}>{formatSize(currentItem.FileSize)}</span>}
                {currentItem.Source === 'upload' && <span className="Triage-item-date">{formatDate(currentItem.ReceivedDate)}</span>}
              </div>
            </div>
          </div>

          {/* Sibling attachments from same email */}
          {siblings.length > 0 && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e8e0d0' }}>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Also from this email ({siblings.length + 1} attachments total)
              </div>
              {siblings.map(sib => (
                <div key={sib.id} style={{ display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '3px 0', fontSize: '0.78rem', color: '#666' }}>
                  <span style={{ flexShrink: 0 }}>{getFileIcon(sib.FileMimeType, sib.Title)}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sib.Title}</span>
                  {sib.FileSize > 0 && <span style={{ color: '#999', flexShrink: 0, fontSize: '0.7rem' }}>{formatSize(sib.FileSize)}</span>}
                </div>
              ))}
            </div>
          )}

          {/* JBB Read Button + Analysis */}
          {(currentItem.FileMimeType || '').includes('pdf') && currentItem.StagingPath && filingStep === 0 && (
            <div style={{ marginTop: '10px', marginBottom: '8px' }}>
              {!jbbAnalysis && !jbbLoading && (
                <button className="Triage-thomas-btn" onClick={handleReadWithJBB}>
                  Read with JBB
                </button>
              )}
              {jbbLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888', fontSize: '0.85rem' }}>
                  <span style={{ display: 'inline-block', animation: 'triageFadeIn 0.5s ease infinite alternate' }}>Reading document...</span>
                </div>
              )}
            </div>
          )}
          {jbbAnalysis && !jbbAnalysis.error && (
            <div style={{ background: '#f5f0e6', border: '1px solid #e0d6c8', borderLeft: '3px solid #B8860B',
              borderRadius: '0 6px 6px 0', padding: '10px 12px', marginBottom: '10px', fontSize: '0.82rem' }}>
              <div style={{ fontWeight: 700, color: '#4B0082', fontSize: '0.75rem', textTransform: 'uppercase',
                letterSpacing: '0.5px', marginBottom: '6px' }}>JBB Analysis</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px' }}>
                {jbbAnalysis.carrierName && <div><span style={{ color: '#888', fontSize: '0.72rem' }}>Carrier:</span> <strong>{jbbAnalysis.carrierName}</strong></div>}
                {jbbAnalysis.policyNumber && <div><span style={{ color: '#888', fontSize: '0.72rem' }}>Policy #:</span> <strong>{jbbAnalysis.policyNumber}</strong></div>}
                {jbbAnalysis.namedInsured && <div><span style={{ color: '#888', fontSize: '0.72rem' }}>Named Insured:</span> <strong>{jbbAnalysis.namedInsured}</strong></div>}
                {jbbAnalysis.documentType && <div><span style={{ color: '#888', fontSize: '0.72rem' }}>Doc Type:</span> <strong>{jbbAnalysis.documentType}</strong></div>}
                {jbbAnalysis.lob && <div><span style={{ color: '#888', fontSize: '0.72rem' }}>LOB:</span> <strong>{jbbAnalysis.lob}</strong></div>}
                {jbbAnalysis.effectiveDate && <div><span style={{ color: '#888', fontSize: '0.72rem' }}>Eff:</span> <strong>{jbbAnalysis.effectiveDate}</strong></div>}
                {jbbAnalysis.expirationDate && <div><span style={{ color: '#888', fontSize: '0.72rem' }}>Exp:</span> <strong>{jbbAnalysis.expirationDate}</strong></div>}
              </div>
              {jbbAnalysis.limits && Object.keys(jbbAnalysis.limits).length > 0 && (
                <div style={{ marginTop: '6px', borderTop: '1px solid #e0d6c8', paddingTop: '4px' }}>
                  <span style={{ color: '#888', fontSize: '0.7rem' }}>Limits:</span>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', marginTop: '2px' }}>
                    {Object.entries(jbbAnalysis.limits).map(([k, v]) => (
                      <div key={k} style={{ fontSize: '0.78rem' }}><span style={{ color: '#666' }}>{k}:</span> {typeof v === 'number' ? `$${v.toLocaleString()}` : v}</div>
                    ))}
                  </div>
                </div>
              )}
              {jbbAnalysis.summary && (
                <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#555', fontStyle: 'italic' }}>{jbbAnalysis.summary}</div>
              )}
              <div style={{ marginTop: '8px' }}>
                <button className="Triage-file-btn Triage-file-btn--go" style={{ fontSize: '0.85rem', padding: '8px 18px' }}
                  onClick={applyJbbAnalysis}>
                  File Document
                </button>
                <button onClick={handleMatchToPolicy} disabled={policyMatchLoading}
                  style={{ background: 'none', border: 'none', color: '#4B0082', fontSize: '0.75rem',
                    cursor: 'pointer', marginLeft: '12px', textDecoration: 'underline',
                    textUnderlineOffset: '2px', fontFamily: 'inherit' }}>
                  {policyMatchLoading ? 'Matching...' : 'or update policy data →'}
                </button>
              </div>
            </div>
          )}

          {/* Policy comparison panel */}
          {policyMatch && !policyMatch.error && (
            <div style={{ background: '#fff', border: '1px solid #e0d6c8', borderLeft: '3px solid #4B0082',
              borderRadius: '0 6px 6px 0', padding: '10px 12px', marginBottom: '10px', fontSize: '0.82rem' }}>

              {policyMatch.policies.length === 0 ? (
                <div>
                  <div style={{ color: '#888', marginBottom: '8px' }}>No matching policy found for "{jbbAnalysis?.namedInsured}"</div>
                  <button className="Triage-file-btn" style={{ fontSize: '0.8rem', padding: '6px 14px' }}
                    onClick={createPolicyFromJBB}>
                    + Create Policy from JBB Data
                  </button>
                </div>
              ) : (
                <>
                  {/* Policy picker if multiple */}
                  {policyMatch.policies.length > 1 && (
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.72rem', color: '#888' }}>Multiple policies found — select one:</span>
                      <select className="Triage-field-input" style={{ marginTop: '4px', fontSize: '0.8rem' }}
                        value={policyMatch.selected?.id || ''}
                        onChange={e => {
                          const pol = policyMatch.policies.find(p => String(p.id) === e.target.value);
                          if (pol) selectMatchPolicy(pol);
                        }}>
                        {policyMatch.policies.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.LOB || 'Policy'} — {p.Carrier || ''} {p.PolicyNumber ? `#${p.PolicyNumber}` : ''} ({p.Status || ''})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Field comparison */}
                  {policyMatch.comparison && (
                    <div>
                      <div style={{ fontWeight: 700, color: '#4B0082', fontSize: '0.72rem', textTransform: 'uppercase',
                        letterSpacing: '0.5px', marginBottom: '6px' }}>
                        Field Comparison
                        {policyMatch.policies.length === 1 && <span style={{ fontWeight: 400, color: '#888', textTransform: 'none' }}>
                          {' '}— {policyMatch.selected?.LOB} {policyMatch.selected?.Carrier} {policyMatch.selected?.PolicyNumber ? `#${policyMatch.selected.PolicyNumber}` : ''}
                        </span>}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '20px 1fr 1fr 1fr', gap: '2px 8px',
                        fontSize: '0.78rem', alignItems: 'center' }}>
                        {/* Header */}
                        <div />
                        <div style={{ fontWeight: 600, fontSize: '0.68rem', color: '#888', textTransform: 'uppercase' }}>Field</div>
                        <div style={{ fontWeight: 600, fontSize: '0.68rem', color: '#888', textTransform: 'uppercase' }}>Policy</div>
                        <div style={{ fontWeight: 600, fontSize: '0.68rem', color: '#888', textTransform: 'uppercase' }}>JBB</div>

                        {policyMatch.comparison.map(row => {
                          const bg = row.status === 'new' ? '#e8f5e9' : row.status === 'conflict' ? '#f0e6ff' : 'transparent';
                          return [
                            <div key={row.key + '_cb'} style={{ background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {row.status !== 'match' && (
                                <input type="checkbox" checked={!!policyMatch.checkedFields[row.key]}
                                  onChange={() => toggleMatchField(row.key)} />
                              )}
                            </div>,
                            <div key={row.key + '_lbl'} style={{ background: bg, padding: '2px 4px', fontWeight: 500 }}>{row.label}</div>,
                            <div key={row.key + '_pol'} style={{ background: bg, padding: '2px 4px', color: row.status === 'match' ? '#999' : '#333' }}>
                              {row.polVal || <span style={{ color: '#ccc' }}>—</span>}
                            </div>,
                            <div key={row.key + '_jbb'} style={{ background: bg, padding: '2px 4px', fontWeight: row.status === 'new' ? 600 : 400,
                              color: row.status === 'match' ? '#999' : '#333' }}>
                              {row.jbbVal}
                              {row.status === 'new' && <span style={{ fontSize: '0.65rem', marginLeft: '4px' }}>NEW</span>}
                              {row.status === 'conflict' && <span style={{ fontSize: '0.65rem', marginLeft: '4px' }}>⚠️</span>}
                              {row.status === 'match' && <span style={{ fontSize: '0.65rem', marginLeft: '4px' }}>✓</span>}
                            </div>,
                          ];
                        })}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button className="Triage-file-btn" style={{ fontSize: '0.8rem', padding: '6px 14px' }}
                          onClick={applySelectedFields}
                          disabled={!Object.values(policyMatch.checkedFields).some(Boolean)}>
                          Apply Selected
                        </button>
                        <button className="Triage-back-btn" style={{ fontSize: '0.8rem' }}
                          onClick={() => setPolicyMatch(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {policyMatch?.error && (
            <div className="Triage-error" style={{ marginBottom: '10px' }}>Policy match failed: {policyMatch.error}</div>
          )}
          {jbbAnalysis?.error && (
            <div className="Triage-error" style={{ marginBottom: '10px' }}>
              JBB read failed: {jbbAnalysis.error}
              <button onClick={() => setJbbAnalysis(null)} style={{ marginLeft: '8px', background: 'none', border: 'none',
                color: '#4B0082', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.8rem' }}>Retry</button>
            </div>
          )}

          {/* Filing breadcrumb */}
          {filingStep > 0 && (
            <div className="Triage-breadcrumb">
              {selectedSide && (
                <span className="Triage-crumb" onClick={() => { resetFiling(); }}>{selectedSide}</span>
              )}
              {selectedEntity && (
                <>
                  <span className="Triage-crumb-sep">&rsaquo;</span>
                  <span className="Triage-crumb" onClick={() => { setFilingStep(1); setSelectedEntity(null); setSelectedDocType(null); }}>
                    {selectedEntity.name}
                  </span>
                </>
              )}
              {selectedDocType && (
                <>
                  <span className="Triage-crumb-sep">&rsaquo;</span>
                  <span className="Triage-crumb" onClick={() => { setFilingStep(2); setSelectedDocType(null); }}>
                    {selectedDocType}
                  </span>
                </>
              )}
              {selectedPolicyDocType && (
                <>
                  <span className="Triage-crumb-sep">&rsaquo;</span>
                  <span className="Triage-crumb">{selectedPolicyDocType}</span>
                </>
              )}
              {selectedLOB && (
                <>
                  <span className="Triage-crumb-sep">&rsaquo;</span>
                  <span className="Triage-crumb">{selectedLOB}</span>
                </>
              )}
            </div>
          )}

          {/* Filing steps */}
          <div className="Triage-filing">
            {/* Step 0: FIG or Family */}
            {filingStep === 0 && (
              <div className="Triage-step">
                <div className="Triage-step-label">FIG or Family?</div>
                <div className="Triage-options">
                  <button className="Triage-option Triage-option--fig" onClick={() => handleSelectSide('FIG')}>
                    <span className="Triage-option-icon">🏢</span>
                    <span>FIG</span>
                  </button>
                  <button className="Triage-option Triage-option--family" onClick={() => handleSelectSide('Family')}>
                    <span className="Triage-option-icon">🏠</span>
                    <span>Family</span>
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Who is this FOR? */}
            {filingStep === 1 && (
              <div className="Triage-step">
                <div className="Triage-step-label">Who is this FOR?</div>
                <div className="Triage-step-hint">Not who sent it — who is it about.</div>
                {selectedSide === 'FIG' && (
                  <input
                    type="text"
                    className="Triage-entity-search"
                    placeholder="Search names..."
                    value={entitySearch}
                    onChange={e => setEntitySearch(e.target.value)}
                    autoFocus
                  />
                )}
                <div className="Triage-entity-list">
                  {selectedSide === 'FIG' && (
                    <button className="Triage-entity-btn" onClick={handleAddClient}
                      style={{ borderStyle: 'dashed', color: '#4B0082', fontWeight: 600, textAlign: 'center', marginBottom: '8px' }}>
                      + Add New Client
                    </button>
                  )}
                  {Object.entries(entityGroups).map(([type, group]) => (
                    <div key={type} className="Triage-entity-group">
                      <div className="Triage-entity-group-label"
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none' }}
                        onClick={() => setCollapsedGroups(prev => ({ ...prev, [type]: !prev[type] }))}>
                        <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: collapsedGroups[type] ? 'rotate(-90deg)' : 'rotate(0deg)', fontSize: '0.6rem' }}>&#9660;</span>
                        {type}s ({group.length})
                      </div>
                      {!collapsedGroups[type] && group.map(entity => (
                        <button key={entity.name} className="Triage-entity-btn" onClick={() => handleSelectEntity(entity)}>
                          {entity.name}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
                <button className="Triage-back-btn" onClick={handleBackStep}>&larr; Back</button>
              </div>
            )}

            {/* Step 2: What kind of document? */}
            {filingStep === 2 && (
              <div className="Triage-step">
                <div className="Triage-step-label">What kind of document?</div>
                <div className="Triage-options Triage-options--doc-types">
                  {docTypes.map(dt => (
                    <button key={dt} className="Triage-option Triage-option--doctype" onClick={() => handleSelectDocType(dt)} title={DOC_TYPE_HINTS[dt] || ''}>
                      <span className="Triage-option-doctype-name">{dt}</span>
                      {DOC_TYPE_HINTS[dt] && <span className="Triage-option-doctype-hint">{DOC_TYPE_HINTS[dt]}</span>}
                    </button>
                  ))}
                </div>
                <button className="Triage-back-btn" onClick={handleBackStep}>&larr; Back</button>
              </div>
            )}

            {/* Step 3: Policy fields (FIG Policies only) */}
            {filingStep === 3 && (
              <div className="Triage-step">
                <div className="Triage-step-label">Policy details</div>
                <div className="Triage-policy-fields">
                  <label className="Triage-field-label">
                    Document Type <span style={{ color: '#c0392b' }}>*</span>
                    <select className="Triage-field-input" value={selectedPolicyDocType}
                      onChange={e => setSelectedPolicyDocType(e.target.value)}>
                      <option value="">Select type...</option>
                      {['Quote', 'Binder', 'Policy', 'Dec Page', 'Endorsement', 'Specimen', 'Schedule', 'Certificate', 'Other'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </label>
                  <label className="Triage-field-label">
                    Line of Business
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <select className="Triage-field-input" style={{ flex: 1 }}
                        value={selectedLOB} onChange={e => setSelectedLOB(e.target.value)}>
                        <option value="">Select LOB...</option>
                        {lobOptions.map(l => (
                          <option key={l.name} value={l.name}>{l.name}{l.abbr ? ` (${l.abbr})` : ''}</option>
                        ))}
                      </select>
                      <button type="button" onClick={handleAddLOB}
                        style={{ background: '#fff', border: '1px solid #4B0082', borderRadius: '6px',
                          color: '#4B0082', width: '36px', height: '36px', fontSize: '1.2rem',
                          fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                        title="Add new LOB">+</button>
                      <button type="button" onClick={() => setShowLOBHelp(true)}
                        style={{ background: '#fff', border: '1px solid #ccc', borderRadius: '50%',
                          color: '#888', width: '28px', height: '28px', fontSize: '0.85rem',
                          fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                        title="View all LOBs">?</button>
                    </div>
                    {showLOBHelp && (
                      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', zIndex: 1000 }}
                        onClick={() => setShowLOBHelp(false)}>
                        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px',
                          maxWidth: '400px', width: '90%', maxHeight: '70vh', overflow: 'auto' }}
                          onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <strong style={{ fontSize: '1rem', color: '#333' }}>Lines of Business</strong>
                            <button onClick={() => setShowLOBHelp(false)}
                              style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#888' }}>&times;</button>
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#555' }}>Abbr</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#555' }}>Line of Business</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lobOptions.map(l => (
                                <tr key={l.name} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '4px 8px', fontWeight: 600, color: '#4B0082' }}>{l.abbr || '—'}</td>
                                  <td style={{ padding: '4px 8px', color: '#333' }}>{l.name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <label className="Triage-field-label" style={{ flex: 1 }}>
                      Effective Date
                      <input type="date" className="Triage-field-input"
                        value={selectedEffDate} onChange={e => setSelectedEffDate(e.target.value)} />
                    </label>
                    <label className="Triage-field-label" style={{ flex: 1 }}>
                      Expiration Date
                      <input type="date" className="Triage-field-input"
                        value={selectedExpDate} onChange={e => setSelectedExpDate(e.target.value)} />
                    </label>
                  </div>
                  <label className="Triage-field-label">
                    Layer
                    <input type="text" className="Triage-field-input" placeholder="e.g. Primary, $5M XS $5M..."
                      value={selectedLayer} onChange={e => setSelectedLayer(e.target.value)} />
                  </label>
                </div>
                <div className="Triage-confirm-row">
                  <button className="Triage-back-btn" onClick={handleBackStep}>&larr; Back</button>
                  <button className="Triage-file-btn" onClick={goToConfirm} disabled={!selectedPolicyDocType}>
                    {selectedPolicyDocType ? 'Continue →' : 'Select document type'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Confirm */}
            {filingStep === 4 && (
              <div className="Triage-step">
                <div className="Triage-step-label">File it?</div>
                <div className="Triage-confirm-summary">
                  <div className="Triage-confirm-path">
                    {selectedSide} &rsaquo; {selectedEntity?.name} &rsaquo; {selectedDocType}
                    {selectedPolicyDocType && <span> &rsaquo; {selectedPolicyDocType}</span>}
                    {selectedLOB && <span> &rsaquo; {selectedLOB}</span>}
                    {(selectedEffDate || selectedExpDate) && <span> &rsaquo; {selectedEffDate}{selectedExpDate ? ` to ${selectedExpDate}` : ''}</span>}
                    {selectedLayer && <span> &rsaquo; {selectedLayer}</span>}
                  </div>
                </div>
                <div style={{ marginTop: '12px', fontSize: '0.8rem', color: '#555' }}>
                  <div style={{ fontWeight: 600, marginBottom: '6px' }}>Already filed here:</div>
                  {existingFilesLoading && <div style={{ color: '#888', fontStyle: 'italic' }}>Checking...</div>}
                  {!existingFilesLoading && existingFiles.length === 0 && (
                    <div style={{ color: '#888', fontStyle: 'italic' }}>No documents filed here yet.</div>
                  )}
                  {!existingFilesLoading && existingFiles.length > 0 && (
                    <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '6px' }}>
                      {existingFiles.map((f, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 4px', borderBottom: i < existingFiles.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                          <span style={{ color: '#888', flexShrink: 0, marginLeft: '8px', fontSize: '0.75rem' }}>
                            {f.lastModifiedDateTime ? new Date(f.lastModifiedDateTime).toLocaleDateString() : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="Triage-confirm-row">
                  <button className="Triage-back-btn" onClick={handleBackStep}>&larr; Back</button>
                  <button className="Triage-file-btn Triage-file-btn--go" onClick={handleFile} disabled={filing}>
                    {filing ? 'Filing...' : 'FILE IT'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Link to policy record */}
            {filingStep === 5 && (
              <div className="Triage-step">
                <div className="Triage-step-label">Link to a policy record?</div>
                <div className="Triage-step-hint">
                  Connect this {selectedPolicyDocType?.toLowerCase() || 'document'} to a policy record in FIG.
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                  <button className="Triage-file-btn" onClick={async () => {
                    try {
                      const token = await getToken();
                      const statusMap = { 'Quote': 'Quote', 'Binder': 'Bound', 'Policy': 'Active' };
                      const newPolicy = await createItem(token, FIG_LISTS.POLICIES, {
                        Title: `${selectedLOB || 'Policy'} - ${selectedPolicyDocType}`,
                        InsuredId: String(selectedEntity.id),
                        LOB: selectedLOB,
                        EffectiveDate: selectedEffDate,
                        ExpirationDate: selectedExpDate,
                        Status: statusMap[selectedPolicyDocType] || 'Quote',
                      });
                      await updateItem(token, 'TriageQueue', filedItemId, { PolicyId: String(newPolicy.id) });
                      setSuccessMsg('Policy created and linked');
                      setTimeout(() => setSuccessMsg(null), 3000);
                      resetFiling();
                    } catch (err) {
                      setError('Failed to create policy: ' + err.message);
                    }
                  }}>
                    + Create New
                  </button>
                  <button className="Triage-back-btn" onClick={resetFiling}>Skip</button>
                </div>

                {linkLoading && <div style={{ color: '#888', fontStyle: 'italic', marginBottom: '12px' }}>Loading policies...</div>}

                {!linkLoading && linkPolicies.length > 0 && (
                  <>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333', marginBottom: '8px' }}>
                      Or link to existing policy:
                    </div>
                    {linkPolicies.map(p => (
                      <button key={p.id} className="Triage-entity-btn" onClick={async () => {
                        try {
                          const token = await getToken();
                          await updateItem(token, 'TriageQueue', filedItemId, { PolicyId: String(p.id) });
                          // Update policy status if binder or policy
                          if (selectedPolicyDocType === 'Binder' && p.Status !== 'Bound') {
                            await updateItem(token, FIG_LISTS.POLICIES, p.id, { Status: 'Bound' });
                          } else if (selectedPolicyDocType === 'Policy' && p.Status !== 'Active') {
                            await updateItem(token, FIG_LISTS.POLICIES, p.id, { Status: 'Active' });
                          }
                          setSuccessMsg(`Linked to ${p.LOB || 'policy'} — ${p.Carrier || ''}`);
                          setTimeout(() => setSuccessMsg(null), 3000);
                          resetFiling();
                        } catch (err) {
                          setError('Failed to link policy: ' + err.message);
                        }
                      }}>
                        <div style={{ fontWeight: 600 }}>{p.LOB || 'Policy'}</div>
                        <div style={{ fontSize: '0.78rem', color: '#888' }}>
                          {p.Carrier || ''} {p.PolicyNumber ? `#${p.PolicyNumber}` : ''}
                          {' — '}{p.Status || ''}
                          {p.EffectiveDate ? ` (${p.EffectiveDate}` : ''}
                          {p.ExpirationDate ? ` to ${p.ExpirationDate})` : p.EffectiveDate ? ')' : ''}
                        </div>
                      </button>
                    ))}
                  </>
                )}

                {!linkLoading && linkPolicies.length === 0 && (
                  <div style={{ color: '#888', fontStyle: 'italic' }}>No existing policies for this insured.</div>
                )}
              </div>
            )}

            {/* Step 6: Post-filing — update policy record with JBB data */}
            {filingStep === 6 && jbbAnalysis && (
              <div className="Triage-step">
                <div className="Triage-step-label">Update policy record?</div>
                <div className="Triage-step-hint">Document filed successfully. Update the policy record with the data JBB extracted?</div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button style={{ background: '#4B0082', color: '#fff', border: 'none', borderRadius: '8px',
                    padding: '10px 20px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}
                    onClick={handleMatchToPolicy} disabled={policyMatchLoading}>
                    {policyMatchLoading ? 'Matching...' : 'Match to Policy'}
                  </button>
                  <button className="Triage-back-btn" onClick={resetFiling}>Skip</button>
                </div>
              </div>
            )}
          </div>

          {/* Skip + nav */}
          {filingStep < 4 && (
            <div className="Triage-skip-row">
              <button className="Triage-skip-btn" onClick={handleSkip}>Skip for now</button>
              {items.length > 1 && (
                <div className="Triage-nav-arrows">
                  <button className="Triage-nav-arrow" onClick={() => { setCurrentIndex(Math.max(0, currentIndex - 1)); resetFiling(); }} disabled={currentIndex === 0}>&lsaquo;</button>
                  <button className="Triage-nav-arrow" onClick={() => { setCurrentIndex(Math.min(items.length - 1, currentIndex + 1)); resetFiling(); }} disabled={currentIndex === items.length - 1}>&rsaquo;</button>
                </div>
              )}
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
}
